/**
 * The canvas's search params — every address the workflow builder answers on
 * declares these, so a deep link into it survives validation.
 *
 * All of them arrive on inbound links from elsewhere in the product. The canvas
 * writes only `actionId`, and otherwise only clears the three it consumes; the
 * start modal also writes `view` and `campaign_started`. They name what the
 * reader should be looking at when the canvas opens:
 *
 * - `ap` / `apf` — which docked panel and which fly-out to open. `ap` is
 *   `trigger`, `action_{id}` (that action's edit panel) or `action_type_{type}`
 *   (the edit panel of the first action of that type); `apf` is `trigger` or
 *   `filters`. Read on arrival and then removed from the URL, because they
 *   describe an arrival rather than a state: left in place, a refresh or a
 *   Back would reopen a surface the reader had since closed.
 * - `actionId` — the action whose edit panel to open. Read once on arrival,
 *   then written by the canvas each time the edit panel moves or closes.
 * - `ipf` — the info panel to open: `goal`, `exit_settings` or
 *   `message_settings`. Read on arrival and while the canvas is open, then
 *   removed from the URL, as `ap` is. Other values are left in the URL. It
 *   wins over an `ap` or `apf` beside it, which stay in the URL unopened, so a
 *   refresh of that URL then opens them.
 * - `campaign_started` — the reader has just started the campaign, which the
 *   canvas acknowledges.
 * - `view` — `review` opens the start modal. The start modal writes and
 *   clears it.
 * - `draft` / `templates` / `attach_experiment` / `action` — entry markers for
 *   the draft session, the templates modal, and attaching an experiment or an
 *   action.
 *
 * Nothing here rejects a value. These are cues from other screens, and an
 * unrecognised one must leave the canvas rendering normally rather than bounce
 * the reader off the URL.
 *
 * ## Only the two consumed params are narrowed
 *
 * `ap` and `apf` are typed as strings because the canvas compares them to the
 * panel names it knows. Every other param is declared but left at whatever type
 * the router parsed it as.
 *
 * That is deliberate, and it is about the URL rather than about the type.
 * The router JSON-parses search on the way in and JSON-stringifies it on the
 * way out, so a param it parsed as the number `5` and something here coerced to
 * the string `'5'` is written back out as `actionId=%225%22`. The canvas rewrites
 * its own search whenever it consumes a panel deep link, so any coercion here
 * would be stamped into the address bar on arrival and would corrupt a cue whose
 * reader has not run yet. Round-tripping the value untouched is what keeps
 * `?actionId=5` reading `?actionId=5` after the trigger params are taken off it.
 *
 * A reader that wants one of those as a string narrows it at the point of use
 * with `searchString`, which is where the coercion belongs: it is then a fact
 * about how that reader reads the value, not a rewrite of the URL.
 */
export type WorkflowCanvasSearch = {
  /**
   * Which docked panel to open on arrival, as the router parsed it:
   * `trigger`, `action_{id}` or `action_type_{type}`.
   */
  ap?: unknown;
  /** Which fly-out to open on arrival, as the router parsed it. */
  apf?: unknown;
  /** The action whose edit panel is open, as the router parsed it. */
  actionId?: unknown;
  /** Which info-panel fly-out to open on arrival, as the router parsed it. */
  ipf?: unknown;
  /** Set when the reader arrives straight from starting the campaign. */
  campaign_started?: unknown;
  /** `review` opens the start modal. */
  view?: unknown;
  /** Entry marker for a draft-editing session. */
  draft?: unknown;
  /** Opens the templates modal on arrival. */
  templates?: unknown;
  /** The experiment to attach. */
  attach_experiment?: unknown;
  /** The action to attach. */
  action?: unknown;
};

/**
 * Search as `validateSearch` receives and returns it: whatever was in the URL,
 * none of it parsed yet.
 */
// oxlint-disable-next-line local-rules/no-unsafe-dictionary-type -- validateSearch is handed unparsed search input, and the pass-through carries those same unvalidated values
type UnvalidatedSearch = Record<string, unknown>;

/**
 * `validateSearch` replaces this route's search, so it returns the whole of what
 * arrived: the canvas's own cues and the global params it does not own
 * (`masq_account_id`, …) alike, each at the type the router parsed it as.
 *
 * The declaration is the work. Without it TanStack drops whatever the route does
 * not name, and every deep link into the canvas silently stops working.
 */
export function validateWorkflowCanvasSearch(search: UnvalidatedSearch): UnvalidatedSearch & WorkflowCanvasSearch {
  return { ...search };
}
